import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Card,
  CardBody,
  Flex,
  Heading,
  Stack,
  Tag,
  TagLabel,
  TagLeftIcon,
  Text,
} from '@chakra-ui/react';
import { CargoType, DocumentType, FreightService } from '@pelicargo/types';
import { Show } from '@pelicargo/ui';
import { ConditionalField, DOCK_HOURS_OPTIONS } from '@pelicargo/utils';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { HiOutlineExclamation } from 'react-icons/hi';

import { API } from '../../utils/api-typings';
import { SimpleDatePickerInput } from '../DatePicker/SimpleDatePickerInput';
import { SimpleDropzone } from '../SimpleDropzone';
import { SimpleInput } from '../SimpleInput';
import { SimpleSelect } from '../SimpleSelect';
import { SimpleTextarea } from '../SimpleTextarea';
import { ConditionalFields } from './ConditionalFieldsForm';
import { PickupAddressForm } from './PickupAddressForm';

type Props = {
  quote: API.GetQuote;
  conditionalFields: ConditionalField[];
};

const requiredIndicatorStyles = { '& .chakra-form__required-indicator': { display: 'none' } };

export const InlandBookingForm = ({ quote, conditionalFields }: Props) => {
  const methods = useFormContext();

  const requestId = quote?.request_id;
  const request = quote?.request;
  const expiresAt = quote?.expires_at;

  const documentsInForm = methods.watch('documents');

  const isDangerousGoods = useMemo(() => request?.cargo_type === CargoType.DangerousGoods, [request?.cargo_type]);
  const shouldShowDgdWarning = useMemo(() => {
    const hasDocument = documentsInForm?.length > 0;

    if (isDangerousGoods && !hasDocument) return true;
    return false;
  }, [documentsInForm, isDangerousGoods]);

  const isExpired = useMemo(() => {
    if (!expiresAt) return false;

    const convertToTZ = (date: Date, timeZone: string) => {
      const newDate = new Date(date.toLocaleString('en-US', { timeZone }));
      return newDate;
    };
    const originTimeZone = quote?.origin_airport?.time_zone;
    const adjustedExpiresAt = convertToTZ(expiresAt, originTimeZone);
    adjustedExpiresAt.setHours(23, 59, 59, 999);
    const now = convertToTZ(new Date(), originTimeZone);

    return adjustedExpiresAt < now;
  }, [expiresAt, quote?.origin_airport?.time_zone]);

  const isFCA = useMemo(
    () => quote?.request?.freight_services?.includes(FreightService.FCA),
    [quote?.request?.freight_services],
  );

  // "Will there be HAWBs?" now lives in Paperwork, so don't also render it as a
  // provider-conditional field.
  const visibleConditionalFields = useMemo(
    () => conditionalFields?.filter((field) => field !== ConditionalField.IS_CONSOLIDATED) ?? [],
    [conditionalFields],
  );
  const showFcaDropoff = !!isFCA;
  const showConditionalFields = visibleConditionalFields.length > 0;

  return (
    <Stack w="full" spacing="3">
      <Show if={!isFCA}>
        <Card borderWidth="thin" boxShadow="none">
          <CardBody p="3">
            <Stack spacing="8">
              <Stack>
                <Heading variant="h5">Pickup</Heading>
                <Heading fontSize="sm" textTransform="uppercase">
                  Details
                </Heading>
                <Stack spacing="4">
                  <Stack>
                    <Stack direction={{ base: 'column', sm: 'row' }} w="full">
                      <Stack direction="row" w="full">
                        <SimpleDatePickerInput
                          name="pickup_ready_at"
                          label="Ready Date"
                          variant="outline"
                          iconFontSize="20px"
                          placeholder="Ready Date"
                          disablePastDates
                          hideLowerErrorMessage
                        />
                      </Stack>
                      <Stack direction="row" align="center" w="full" spacing="1">
                        <SimpleSelect
                          name="from_dock_hours"
                          label="Dock Hours"
                          options={DOCK_HOURS_OPTIONS}
                          variant="outline"
                          hideLowerErrorMessage
                        />
                        <Text>-</Text>
                        <SimpleSelect
                          name="to_dock_hours"
                          label="Dock Hours"
                          options={DOCK_HOURS_OPTIONS}
                          variant="outline"
                          hideLowerErrorMessage
                        />
                      </Stack>
                    </Stack>
                    <SimpleInput
                      name="pickup_ref"
                      label="Pickup Reference # (Optional)"
                      placeholder="x x x   x x x   x x x"
                      variant="outline"
                    />
                  </Stack>
                  <PickupAddressForm />
                </Stack>
              </Stack>
              <Stack sx={requiredIndicatorStyles}>
                <Heading fontSize="sm" textTransform="uppercase">
                  Contact
                </Heading>
                <Stack direction="row" w="full">
                  <SimpleInput
                    name="inland_dropoff_contact.full_name"
                    label="Full Name"
                    placeholder="Full Name"
                    flex="1"
                    isRequired
                    hideLowerErrorMessage
                  />
                  <SimpleInput
                    name="inland_dropoff_contact.phone_number"
                    label="Phone"
                    placeholder="Phone"
                    flex="1"
                    isRequired
                    hideLowerErrorMessage
                  />
                  <SimpleInput
                    name="inland_dropoff_contact.email"
                    label="Email (Optional)"
                    placeholder="Email"
                    flex="1"
                  />
                </Stack>
              </Stack>
            </Stack>
          </CardBody>
        </Card>
      </Show>
      <Show if={showFcaDropoff}>
        <Card borderWidth="thin" boxShadow="none">
          <CardBody p="3">
            <Stack>
              <Heading variant="h5">Drop-off</Heading>
              <SimpleDatePickerInput
                name="dropoff_at"
                label="Drop-off date"
                variant="outline"
                iconFontSize="20px"
                placeholder="Drop-off date"
                disablePastDates
              />
              <SimpleInput
                name="trucking_name"
                label="Trucking Company (Optional)"
                placeholder="Trucking Company"
                flex="1"
                variant="outline"
              />
            </Stack>
          </CardBody>
        </Card>
      </Show>

      <Show if={showConditionalFields}>
        <Card borderWidth="thin" boxShadow="none">
          <CardBody p="3">
            <ConditionalFields quote={quote} fields={visibleConditionalFields} />
          </CardBody>
        </Card>
      </Show>
      <Card borderWidth="thin" boxShadow="none">
        <CardBody p="3">
          <Stack spacing="2">
            <Stack>
              <Heading variant="h5">Additional Information (Optional)</Heading>
              <SimpleInput
                label="Your Reference #"
                name="customer_ref"
                placeholder="x x x   x x x   x x x"
                variant="outline"
                flex="1"
              />
            </Stack>
            <SimpleTextarea
              name="additional_information"
              label="Notes"
              placeholder="Notes"
              variant="outline"
              rows={2}
            />
          </Stack>
        </CardBody>
      </Card>

      <Show if={shouldShowDgdWarning}>
        <Flex>
          <Tag size="lg" borderRadius="full" variant="outline" colorScheme="red" py="2">
            <TagLeftIcon as={HiOutlineExclamation} boxSize="6" />
            <TagLabel fontSize="lg">Missing Dangerous Goods Declaration - Upload to Book</TagLabel>
          </Tag>
        </Flex>
      </Show>
      <Show if={isDangerousGoods}>
        <SimpleDropzone name="documents" documentType={DocumentType.DGD} requestId={requestId} />
      </Show>
      <Show if={isExpired}>
        <Flex>
          <Alert status="error" variant="subtle" fontSize="sm" borderRadius="md">
            <AlertIcon />
            <Stack spacing="1">
              <AlertTitle>Quote Expired</AlertTitle>
              <AlertDescription>
                This quote has passed its expiry date. Booking this may not result in the same price.
              </AlertDescription>
            </Stack>
          </Alert>
        </Flex>
      </Show>
    </Stack>
  );
};
